import { Link, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { useAuthOrSession } from '../../lib/authOrSession';
import { useUserRole } from '../../lib/useUserRole';
import { Footer } from './Footer';
import { SkipToContent } from '../a11y/SkipToContent';

interface AppFrameProps {
  children: ReactNode;
  /**
   * Wenn true, wird kein Container-Padding gesetzt — die Kindkomponente
   * verantwortet ihr eigenes Layout (Hub macht das mit max-w-3xl).
   */
  bare?: boolean;
}

/**
 * Top-Logo-Bar + zentrierter Editorial-Container + dezenter Footer.
 *
 * Keine Sidebar im Mandanten-Flow (das wäre Cockpit-spezifisch). Die
 * Wortmarke „Petschwork" sitzt links in serif-light-Schnitt; rechts kommt –
 * sobald eingeloggt – ein dezenter Status-/Logout-Bereich. Der Auth-Status
 * läuft über `useAuthOrSession` — dieselbe Quelle wie RequireAuth, sodass
 * Mock und Supabase identisch behandelt werden.
 */
export function AppFrame({ children, bare = false }: AppFrameProps) {
  const { user, signOut } = useAuthOrSession();
  const { role, isAdmin } = useUserRole();
  const navigate = useNavigate();

  // Rollen-Trennung im Header: Berater/Admin bekommen die Cockpit-Navigation
  // (Wortmarke → Mandatsliste, „Profil" = Berater-Briefkopf). Mandanten dürfen
  // in KEINE Berater-Bereiche geführt werden — für sie (und den kurzen
  // Lade-Zustand role=null) ist die Wortmarke nicht verlinkt und es gibt keinen
  // Profil-Link.
  const isStaff = role === 'berater' || role === 'admin';

  return (
    <div className="flex min-h-screen flex-col bg-white text-stone-900">
      <SkipToContent />
      <header
        className="sticky top-0 z-10 border-b border-stone-200 bg-white/80 backdrop-blur-sm"
        style={{ height: 'var(--header-h)' }}
      >
        <div className="mx-auto flex h-full max-w-3xl items-center justify-between px-5 md:px-8">
          {isStaff || !user ? (
            <Link
              to={user ? '/cases' : '/'}
              className="group flex items-baseline gap-2 outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <span className="font-serif text-xl font-black tracking-tight text-stone-900 group-hover:text-stone-700">
                {de.brand.wordmark}
              </span>
              <span className="hidden font-serif text-xs italic text-stone-500 sm:inline">
                {de.brand.tagline}
              </span>
            </Link>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-xl font-black tracking-tight text-stone-900">
                {de.brand.wordmark}
              </span>
              <span className="hidden font-serif text-xs italic text-stone-500 sm:inline">
                {de.brand.tagline}
              </span>
            </div>
          )}
          {user && (
            <div className="flex items-center gap-4">
              <span className="hidden font-sans text-xs font-medium uppercase tracking-widest text-stone-500 sm:inline">
                {user.email}
              </span>
              {isAdmin && (
                <Link
                  to="/admin/settings"
                  aria-label="Administration · System-Einstellungen"
                  className="inline-flex items-center gap-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:underline"
                >
                  <SettingsIcon size={12} aria-hidden="true" />
                  Admin
                </Link>
              )}
              {isStaff && (
                <Link
                  to="/profile"
                  className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:text-stone-900 hover:underline"
                >
                  {de.nav.profile}
                </Link>
              )}
              <button
                type="button"
                onClick={() => {
                  void Promise.resolve(signOut()).then(() => {
                    void navigate('/');
                  });
                }}
                className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:text-stone-900 hover:underline"
              >
                {de.nav.logout}
              </button>
            </div>
          )}
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
        {bare ? children : <div className="mx-auto max-w-3xl px-5 md:px-8">{children}</div>}
      </main>

      <Footer />
    </div>
  );
}
